/**
 * Polling helper for background LLM jobs (see backend routers/llm_jobs.py).
 *
 * Pattern:
 *   const { result } = await runAsyncJob(api, {
 *     method: 'post',
 *     url: `/businesses/${id}/investor-scenarios/generate`,
 *     params: { async: 1 },
 *     data: { roi_types, horizons },
 *     onStatus: (status, job) => setProgress(status),
 *   });
 *
 * Why it exists: heavy LLM calls used to hold open the HTTP request for
 * 30-90s, exhausting FastAPI worker slots under concurrent load. The
 * async path returns a job_id immediately; we poll until done/failed and
 * surface the final result (or throw a synthetic Axios-shaped error so
 * existing showApiError() handling keeps working).
 *
 * Polling cost reduction (Feb 2026 — part of the 500-user scaling work):
 *   - Default cadence raised from 2s → 5s (60% drop in poll volume).
 *   - When the business WebSocket is open, the backend pushes
 *     `llm_job_update` events on every status transition. `useBusinessSocket`
 *     re-broadcasts these as a window-level `tlb:ws_event` CustomEvent;
 *     here we listen for the matching job_id and short-circuit the next
 *     sleep so users see results within ~100ms of the LLM finishing,
 *     regardless of where in the 5s poll cycle they are. Polling stays
 *     as a fallback for sessions where the WS isn't connected.
 */
import { api } from "./apiClient";
import { toast } from "sonner";
import { trackEvent } from "./telemetry";
import { useEffect, useState } from "react";

const DEFAULT_POLL_MS = 5000;
const DEFAULT_MAX_WAIT_MS = 5 * 60 * 1000; // 5 minutes — caps runaway polls

// Map URL endpoint suffix → `llm_jobs.kind`. Used by the 502 circuit
// breaker below to recover an orphaned job when the K8s ingress times
// out before the backend's `{job_id}` response makes it back to us. The
// 100-user stress test (Feb 2026) showed up to 45 jobs running on the
// backend that the client never saw — without this recovery, a retry
// would double-bill LLM tokens.
const ENDPOINT_TO_KIND = [
  [/\/businesses\/[^/]+\/generate$/, "generate_plan"],
  [/\/businesses\/[^/]+\/generate\/plan$/, "generate_plan_only"],
  [/\/businesses\/[^/]+\/generate\/roadmap$/, "generate_roadmap"],
  [/\/businesses\/[^/]+\/generate\/plan-essentials$/, "plan_essentials"],
  [/\/businesses\/[^/]+\/health-check$/, "health_check"],
  [/\/businesses\/[^/]+\/compliance\/generate$/, "compliance_generate"],
  [/\/businesses\/[^/]+\/grants\/match$/, "grants_match"],
  [/\/businesses\/[^/]+\/investor-scenarios\/generate$/, "investor_generate"],
  [/\/businesses\/[^/]+\/performance\/monthly-review$/, "monthly_review"],
];

// kind → friendly progress label for the generation banner/toast.
const PROGRESS_LABELS = {
  generate_plan: "Building your business plan",
  generate_plan_only: "Regenerating your plan",
  generate_roadmap: "Mapping your launch roadmap",
  plan_essentials: "Building your business plan",
  health_check: "Running your plan health check",
  compliance_generate: "Generating your compliance checklist",
  grants_match: "Matching grants for you",
  investor_generate: "Modeling investor scenarios",
  monthly_review: "Writing your monthly review",
};

function _deriveBusinessId(url) {
  const m = String(url || "").match(/\/businesses\/([^/]+)\//);
  return m ? m[1] : null;
}

function _deriveKind(url, override) {
  if (override) return override;
  const s = String(url || "");
  for (const [re, kind] of ENDPOINT_TO_KIND) {
    if (re.test(s)) return kind;
  }
  return null;
}

/** Try to find an orphaned in-flight job for this (business, kind) tuple.
 *  Called after the initial POST returns a 5xx — the backend may have
 *  enqueued the job but the response got dropped by the ingress.
 *  Returns a `job_id` on hit, null on miss. */
async function _recoverOrphanJobId(client, businessId, kind) {
  if (!businessId || !kind) return null;
  try {
    const { data } = await client.get("/llm-jobs", {
      params: { business_id: businessId, kind, active_only: true, limit: 1 },
    });
    const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
    return jobs.length ? jobs[0].job_id : null;
  } catch {
    return null;
  }
}

/** Resolve after `ms` ms OR as soon as the business WS pushes a status
 *  change for this job_id — whichever comes first. */
function _interruptibleSleep(ms, jobId) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      try {
        window.removeEventListener("tlb:ws_event", onEvt);
      } catch {
        /* noop */
      }
      clearTimeout(timer);
      resolve();
    };
    const onEvt = (e) => {
      const d = e?.detail;
      if (!d || d.type !== "llm_job_update") return;
      if (d.job_id && d.job_id !== jobId) return;
      // Any status update for our job is a reason to poll immediately.
      finish();
    };
    try {
      window.addEventListener("tlb:ws_event", onEvt);
    } catch {
      /* CustomEvent unsupported — pure timer fallback */
    }
    const timer = setTimeout(finish, ms);
  });
}

// ---------------------------------------------------------------------------
// Global in-flight generation tracker. Tours (and anything else) can ask
// "is a Three Little Birds generation running right now?" without threading props around.
let _activeJobs = 0;
let _latestHint = "";
const _emitActiveJobs = () => {
  try {
    window.dispatchEvent(new CustomEvent("tlb:llm_active_jobs", { detail: { count: _activeJobs, hint: _latestHint } }));
  } catch {
    /* non-browser environment */
  }
};
const _setProgressHint = (hint) => {
  _latestHint = hint || "";
  _emitActiveJobs();
};
export const getActiveLlmJobCount = () => _activeJobs;

// React hook: `{ active, hint }` — active while any runAsyncJob generation is
// in flight; hint carries the same live ETA line the progress toast shows.
export function useLlmGenerationActive() {
  const [state, setState] = useState(() => ({ active: _activeJobs > 0, hint: _latestHint }));
  useEffect(() => {
    const onChange = (e) => setState({ active: (e?.detail?.count ?? 0) > 0, hint: e?.detail?.hint || "" });
    window.addEventListener("tlb:llm_active_jobs", onChange);
    return () => window.removeEventListener("tlb:llm_active_jobs", onChange);
  }, []);
  return state;
}

export async function runAsyncJob(axiosInstance, opts) {
  _activeJobs += 1;
  _emitActiveJobs();
  try {
    return await _runAsyncJob(axiosInstance, opts);
  } finally {
    _activeJobs = Math.max(0, _activeJobs - 1);
    if (_activeJobs === 0) _latestHint = "";
    _emitActiveJobs();
  }
}

async function _runAsyncJob(
  axiosInstance,
  {
    method = "post",
    url,
    params = {},
    data,
    pollMs = DEFAULT_POLL_MS,
    maxWaitMs = DEFAULT_MAX_WAIT_MS,
    onStatus,
    kind: kindOverride,  // optional explicit `llm_jobs.kind` for the 502 recovery
    showProgress = true, // drive a global generation-progress toast (queue + ETA)
  }
) {
  const client = axiosInstance || api;
  let jobId = null;
  let startData = null;
  const _progStart = Date.now();
  const _renderProgress = (job) => {
    if (!showProgress || !jobId) return;
    const k = job?.kind || _deriveKind(url, kindOverride);
    const label = PROGRESS_LABELS[k] || "Generating with Three Little Birds…";
    const pos = job?.queue_position || 0;
    let description;
    if (job?.status === "queued" && pos > 1) {
      const w = job?.estimated_wait_seconds || 0;
      description = `Queued · #${pos} in line${w ? ` · ~${w}s to start` : " · starting shortly"}`;
    } else {
      const total = job?.estimated_total_seconds || 40;
      const elapsed = Math.round((Date.now() - _progStart) / 1000);
      const remain = Math.max(3, total - elapsed);
      description = `Crunching the numbers… about ${remain}s left`;
    }
    _setProgressHint(description);
    toast.loading(label, {
      id: `llm-progress-${jobId}`,
      description,
      duration: Infinity,
    });
  };
  try {
    const startResp = await client.request({
      method,
      url,
      params: { ...params, async: 1 },
      data,
    });
    startData = startResp?.data;
    jobId = startData?.job_id;
  } catch (e) {
    // Circuit breaker — the initial POST failed before we got a job_id.
    // If the ingress 502'd (or the upstream returned a transient 5xx),
    // the backend may have already enqueued the job; recover it instead
    // of bubbling the failure and risking a duplicate retry.
    const status = e?.response?.status;
    if (status === 502 || status === 503 || status === 504) {
      const businessId = _deriveBusinessId(url);
      const kind = _deriveKind(url, kindOverride);
      const recovered = await _recoverOrphanJobId(client, businessId, kind);
      if (recovered) {
        jobId = recovered;
        startData = { job_id: recovered, status: "queued", kind, recovered: true };
        // Telemetry — quantifies how often the circuit breaker fires
        // in prod so we can measure the real-world impact of the
        // uvicorn-workers scale-up once Emergent bumps the config.
        trackEvent(
          "llm_job_recovered",
          { kind: kind || "unknown", status_code: status },
          { businessId: businessId || undefined },
        );
        // Surface the recovery to the founder so the temporary 502
        // hiccup feels handled rather than invisible. Without this
        // toast, the UI would just keep its existing "Generating…"
        // spinner — the user has no idea we caught the failure and
        // resumed against the in-flight job.
        toast.info("🔄 We're catching up — your Three Little Birds job is still running", {
          description: "We hit a brief network hiccup but recovered your job. Hang tight.",
          duration: 6000,
          id: `llm-job-recovered-${recovered}`,  // dedupe across re-renders
        });
      }
    }
    if (!jobId) throw e;
  }
  if (!jobId) {
    // Backend didn't take the async path (e.g. ?async=1 not wired on this
    // endpoint) — caller still gets the synchronous payload.
    return { result: startData, job: null };
  }
  onStatus?.(startData?.status || "queued", startData);
  _renderProgress(startData || { status: "queued" });

  const deadline = Date.now() + maxWaitMs;
  try {
    while (Date.now() < deadline) {
      await _interruptibleSleep(pollMs, jobId);
      let job;
      try {
        const { data: j } = await client.get(`/llm-jobs/${jobId}`);
        job = j;
      } catch (e) {
        // Transient poll error — retry next tick unless it's clearly fatal.
        if (e?.response?.status === 404 || e?.response?.status === 401) {
          throw e;
        }
        continue;
      }
      onStatus?.(job.status, job);
      _renderProgress(job);
      if (job.status === "done") {
        return { result: job.result, job };
      }
      if (job.status === "failed") {
        // Reconstruct a thrown Axios-like error so existing toast helpers
        // (showApiError) render the same UX as the sync path.
        const err = new Error(job.error?.detail || "Three Little Birds job failed");
        err.response = {
          status: job.error?.status_code || 500,
          data: { detail: job.error?.detail || "Three Little Birds job failed" },
          headers: {},
        };
        throw err;
      }
    }
    throw new Error("Three Little Birds job timed out — please try again.");
  } finally {
    // Always clear the progress toast on any exit (success, failure, timeout).
    if (showProgress && jobId) toast.dismiss(`llm-progress-${jobId}`);
  }
}
